import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { Sidebar } from '../components/Sidebar';
import { sidebarActions } from '../utils/sidebarActions';
import { SidebarLogo } from './sidebar/conversations';
import { SidebarFooterContent } from './sidebar/footer';
import { FriendsPanel } from './sidebar/friends';
import { InboxPanel } from './sidebar/inbox';
import { FolderPanel } from './sidebar/folderPanel';
import { ChatInvitationsPanel } from './sidebar/invitations';
import { SidebarTopNavContent, SidebarNavContent, type SidebarVariant } from './sidebar/nav';
import { useConversationFolders } from '../hooks/useConversationFolders';
import { SidebarListViewProvider } from './sidebar/sidebarListView';
import { InvitationInboxProvider } from '../hooks/useInvitationInbox';
import {
  applySidebarAction,
  closeChatInvitesPanel,
  closeFolderPanel,
  closeFriendsPanel,
  closeInboxPanel,
  initialSidebarPanelState,
  openFolderPanel,
  toggleChatInvitesPanel,
  toggleFriendsPanel,
  toggleInboxPanel,
} from './sidebar/sidebarPanelState';

/**
 * Main application sidebar with navigation links.
 * Shared across all platforms (web, desktop, mobile).
 *
 * When variant is 'public', authenticated-only sections (friends, conversations,
 * identity/account flyouts) are omitted and a login prompt is shown instead.
 */
interface AppSidebarProps {
  onExpandedChange?: (expanded: boolean) => void;
  variant?: SidebarVariant;
}

export function AppSidebar({ onExpandedChange, variant = 'full' }: AppSidebarProps) {
  const [panelState, setPanelState] = useState(initialSidebarPanelState);
  const isPublic = variant === 'public';

  const handleToggleFriendsPanel = useCallback(() => {
    setPanelState(toggleFriendsPanel);
  }, []);

  const handleCloseFriendsPanel = useCallback(() => {
    setPanelState(closeFriendsPanel);
  }, []);

  const handleToggleInboxPanel = useCallback(() => {
    setPanelState(toggleInboxPanel);
  }, []);

  const handleCloseInboxPanel = useCallback(() => {
    setPanelState(closeInboxPanel);
  }, []);

  const handleToggleChatInvitesPanel = useCallback(() => {
    setPanelState(toggleChatInvitesPanel);
  }, []);

  const handleCloseChatInvitesPanel = useCallback(() => {
    setPanelState(closeChatInvitesPanel);
  }, []);

  const handleOpenFolder = useCallback((folderId: string) => {
    setPanelState((s) => openFolderPanel(s, folderId));
  }, []);

  const handleCloseFolder = useCallback(() => {
    setPanelState(closeFolderPanel);
  }, []);

  useEffect(() => {
    return sidebarActions.subscribe((action) => {
      setPanelState((prevState) => applySidebarAction(prevState, action));
    });
  }, []);

  const authenticatedChrome = (children: ReactNode) =>
    isPublic ? children : (
      <SidebarListViewProvider>
        <InvitationInboxProvider>{children}</InvitationInboxProvider>
      </SidebarListViewProvider>
    );

  return authenticatedChrome(
    <Sidebar
      header={<SidebarLogo />}
      topNav={
        <SidebarTopNavContent
          isFriendsPanelOpen={panelState.isFriendsPanelOpen}
          onToggleFriendsPanel={handleToggleFriendsPanel}
          variant={variant}
        />
      }
      footer={
        <SidebarFooterContent
          variant={variant}
          isInboxPanelOpen={panelState.isInboxPanelOpen}
          onToggleInboxPanel={handleToggleInboxPanel}
        />
      }
      panel={
        isPublic ? undefined : (
          <>
            <FriendsPanel
              isOpen={panelState.isFriendsPanelOpen}
              onClose={handleCloseFriendsPanel}
            />
            <InboxPanel
              isOpen={panelState.isInboxPanelOpen}
              onClose={handleCloseInboxPanel}
            />
            <ChatInvitationsPanel
              isOpen={panelState.isChatInvitesPanelOpen}
              onClose={handleCloseChatInvitesPanel}
            />
            <FolderPanelConnector
              openFolderId={panelState.openFolderId}
              onClose={handleCloseFolder}
            />
          </>
        )
      }
      onExpandedChange={onExpandedChange}
    >
      {!isPublic && (
        <SidebarNavContent
          isChatInvitesPanelOpen={panelState.isChatInvitesPanelOpen}
          onToggleChatInvitesPanel={handleToggleChatInvitesPanel}
          onOpenFolder={handleOpenFolder}
        />
      )}
    </Sidebar>,
  );
}

/**
 * Resolves the folder object from the openFolderId and renders FolderPanel.
 * Separated to avoid calling useConversationFolders in the public variant.
 */
function FolderPanelConnector({
  openFolderId,
  onClose,
}: {
  openFolderId: string | null;
  onClose: () => void;
}) {
  const { folders } = useConversationFolders();
  const folder = useMemo(
    () => (openFolderId ? folders.find((f) => f.id === openFolderId) ?? null : null),
    [openFolderId, folders],
  );

  return (
    <FolderPanel
      isOpen={!!openFolderId}
      folder={folder}
      onClose={onClose}
    />
  );
}
